"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const faqs = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Reading?",
  answer: "Yes. We regularly provide scaffolding services across Reading, Caversham, Tilehurst, Earley, Woodley, and surrounding Berkshire areas for residential, commercial, and industrial sites."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Reading?",
  answer: "We supply independent access scaffolds, temporary roofs, edge protection, chimney stacks, birdcage scaffolding, and bespoke access designs engineered to compliant safety standards."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We frequently partner with roofing contractors and solar installers, building secure perimeter edge protection, handrails, chimney scaffolds, and weather-proof temporary roof structures."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We collaborate with main contractors, developers, local authorities, and facilities managers. Our operations are supported by NASC and CHAS accreditations with complete RAMS documentation."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Pete Wood and our team have over 20 years of hands-on experience designing access solutions for narrow side alleys, uneven ground, tight property boundaries, and public thoroughfares."
}, {
  id: "item-6",
  question: "How do I request a quote in Reading?",
  answer: "You can send your project details, site address, and any available architectural drawings or photos through our online quote request form. We review the specification and provide an itemised proposal."
}];
export default function Readingfaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="readingfaq" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-3xl mx-auto mb-14 md:mb-18">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="px-3 py-1 text-xs font-medium uppercase tracking-wider gap-1.5 border-border">
              <HelpCircle className="size-3.5 text-primary" />
              <span>Reading & Berkshire</span>
            </Badge>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4 font-default">
            Frequently asked questions
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-default">
            Clear information on our coverage, access capabilities, and safety accreditations across Reading and Berkshire.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Main FAQ Accordion */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-8 bg-card text-card-foreground rounded-xl border border-border p-6 md:p-8 shadow-sm">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
              {faqs.map((faq, i) => <div key={faq.id} data-index={i}>
                  <AccordionItem value={faq.id} className="border-border py-1">
                    <AccordionTrigger className="text-left font-semibold text-base md:text-lg hover:text-primary transition-[color] py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm md:text-base leading-relaxed pr-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </motion.div>

          {/* Quick Help & Accreditations Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }} className="lg:col-span-4 flex flex-col gap-6">
            <div className="bg-muted text-foreground p-6 md:p-8 rounded-xl border border-border">
              <h3 className="text-lg font-bold text-foreground mb-2 font-default">
                Have a specific project in Reading?
              </h3>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Send your site details, measurements, or project scope to discuss access requirements with our team.
              </p>
              <Button asChild className="w-full justify-between" size="lg">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </div>

            <div className="bg-card text-card-foreground p-6 rounded-xl border border-border shadow-xs">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="size-5 text-primary shrink-0" />
                <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                  Verified Accreditations
                </h4>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Fully accredited with NASC and CHAS. Comprehensive site-specific RAMS supplied prior to erection.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}